import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Loader2,
  Pencil,
  PenTool,
  Redo2,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type {
  FieldSource,
  FieldType,
  ReadTemplateField,
} from '@/services/api/generated/templatesV2Service';
import { useTemplateFieldsQuery } from '@/services/api/generated/templatesV2Service';
import {
  useTemplateContextFieldsQuery,
  useTemplateFieldDeleteMutation,
  useTemplateFieldUpdateMutation,
} from '@/services/api/templatesV2ServiceEnhanced';

import { TemplateFieldEditor } from './TemplateFieldEditor';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

interface TemplateFieldsPanelProps {
  templateId: string;
  templateFormat?: string;
  readOnly?: boolean;
  onReanalyze?: () => void;
  isReanalyzing?: boolean;
}

const TYPE_CONFIG: Record<FieldType, { label: string; className: string }> = {
  TEXT: {
    label: 'Text',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  },
  DATE: {
    label: 'Date',
    className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  },
  CHECKBOX: {
    label: 'Check',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  IMAGE: {
    label: 'Image',
    className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  },
  ARRAY: {
    label: 'Array',
    className: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  },
  SIGNATURE: {
    label: 'Sig',
    className: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  },
  INITIALS: {
    label: 'Init',
    className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  },
};

// ---------------------------------------------------------------------------
// Pending change type
// ---------------------------------------------------------------------------

type PendingFieldChange = {
  source: FieldSource;
  context_key: string | null;
};

// ---------------------------------------------------------------------------
// Context field picker types
// ---------------------------------------------------------------------------

type ContextFieldOption = { id: string; name: string };
type ContextFieldGroup = { label: string; options: ContextFieldOption[] };

// ---------------------------------------------------------------------------
// InlineContextKeyPicker — compact popover combobox for assigning context key
// ---------------------------------------------------------------------------

const InlineContextKeyPicker = ({
  groups,
  selectedKey,
  variant = 'map',
  onSelect,
}: {
  groups: ContextFieldGroup[];
  selectedKey?: string | null;
  variant?: 'map' | 'remap';
  onSelect: (key: string) => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        {variant === 'map' ? (
          <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-400 dark:hover:bg-amber-900/40"
          >
            Map
            <ChevronsUpDown className="h-3 w-3 opacity-60" />
          </button>
        ) : (
          <button
            type="button"
            className="rounded p-1 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
            title="Remap field"
          >
            <Redo2 className="h-3 w-3" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[28rem] p-0 bg-white" align="start">
        <Command>
          <CommandInput placeholder="Search fields..." className="border-0" />
          <CommandList className="max-h-[20rem]">
            <CommandEmpty className="py-2 px-4 text-sm font-medium text-gray-500">
              No matching fields.
            </CommandEmpty>
            {groups.map((group, i) => (
              <div key={group.label}>
                {i > 0 && <CommandSeparator />}
                <CommandGroup
                  heading={group.label}
                  className="px-2 py-1.5 text-xs font-medium text-gray-500"
                >
                  {group.options.map((opt) => (
                    <CommandItem
                      key={opt.id}
                      value={opt.name}
                      onSelect={() => {
                        onSelect(opt.id);
                        setOpen(false);
                      }}
                      className="flex items-center px-2 py-1.5 text-sm font-medium rounded-sm cursor-pointer hover:bg-gray-100 aria-selected:bg-gray-100 data-[selected=true]:bg-gray-100"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-3.5 w-3.5 shrink-0',
                          opt.id === selectedKey ? 'opacity-100 text-primary' : 'opacity-0',
                        )}
                      />
                      <span className="break-words">{opt.name}</span>
                      <span className="ml-auto shrink-0 pl-3 text-[11px] font-medium text-zinc-500">
                        {opt.id}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </div>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// ---------------------------------------------------------------------------
// FieldItem — compact single-row display
// ---------------------------------------------------------------------------

const FieldItem = ({
  field,
  readOnly,
  hasPendingChange,
  contextFieldGroups,
  onEdit,
  onDelete,
  onAssignContextKey,
  onUnmap,
}: {
  field: ReadTemplateField;
  readOnly?: boolean;
  hasPendingChange?: boolean;
  contextFieldGroups?: ContextFieldGroup[];
  onEdit?: (field: ReadTemplateField) => void;
  onDelete?: (field: ReadTemplateField) => void;
  onAssignContextKey?: (field: ReadTemplateField, contextKey: string) => void;
  onUnmap?: (field: ReadTemplateField) => void;
}) => {
  const isManual = field.source === 'MANUAL';
  const isContext = field.source === 'CONTEXT';
  const typeConfig = TYPE_CONFIG[field.field_type] ?? {
    label: field.field_type,
    className: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
  };

  // Show mapping arrow when reference differs from context_key
  const showMapping =
    isContext && field.context_key && field.reference !== field.context_key;

  return (
    <div
      className={cn(
        'group flex items-center gap-2 rounded-md px-2.5 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-800/40',
        hasPendingChange && 'bg-blue-50/50 dark:bg-blue-900/10',
      )}
    >
      {/* Pending change indicator */}
      {hasPendingChange && (
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />
      )}

      {/* Type badge */}
      <span
        className={cn(
          'inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
          typeConfig.className,
        )}
      >
        {typeConfig.label}
      </span>

      {/* Reference name, with optional mapping to context_key */}
      <code className="min-w-0 truncate font-mono text-xs font-medium text-zinc-900 dark:text-zinc-100">
        {field.reference}
      </code>
      {showMapping && (
        <>
          <span className="shrink-0 text-[11px] font-medium text-zinc-500">
            =&gt;
          </span>
          <code className="min-w-0 truncate font-mono text-xs font-medium text-zinc-600 dark:text-zinc-400">
            {field.context_key}
          </code>
        </>
      )}

      {/* Inline context key picker for unrecognized / manual fields */}
      {isManual && !readOnly && contextFieldGroups && contextFieldGroups.length > 0 && (
        <InlineContextKeyPicker
          groups={contextFieldGroups}
          onSelect={(key) => onAssignContextKey?.(field, key)}
        />
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Hover actions */}
      {!readOnly && (
        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          {/* Remap picker for CONTEXT fields */}
          {isContext && contextFieldGroups && contextFieldGroups.length > 0 && (
            <InlineContextKeyPicker
              groups={contextFieldGroups}
              selectedKey={field.context_key}
              variant="remap"
              onSelect={(key) => onAssignContextKey?.(field, key)}
            />
          )}
          {/* Unmap button for CONTEXT fields */}
          {isContext && onUnmap && (
            <button
              type="button"
              onClick={() => onUnmap(field)}
              className="rounded p-1 text-zinc-500 hover:bg-amber-50 hover:text-amber-600 dark:hover:bg-amber-900/20 dark:hover:text-amber-400"
              title="Unmap field"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onEdit?.(field)}
            className="rounded p-1 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
            title="Edit field"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(field)}
            className="rounded p-1 text-zinc-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
            title="Delete field"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// FieldList — renders a list of fields
// ---------------------------------------------------------------------------

const FieldList = ({
  fields,
  readOnly,
  pendingFieldIds,
  contextFieldGroups,
  onEdit,
  onDelete,
  onAssignContextKey,
  onUnmap,
}: {
  fields: ReadTemplateField[];
  readOnly?: boolean;
  pendingFieldIds?: Set<string>;
  contextFieldGroups?: ContextFieldGroup[];
  onEdit?: (field: ReadTemplateField) => void;
  onDelete?: (field: ReadTemplateField) => void;
  onAssignContextKey?: (field: ReadTemplateField, contextKey: string) => void;
  onUnmap?: (field: ReadTemplateField) => void;
}) => (
  <div className="space-y-px">
    {fields.map((field) => (
      <FieldItem
        key={field.id}
        field={field}
        readOnly={readOnly}
        hasPendingChange={pendingFieldIds?.has(field.id as string)}
        contextFieldGroups={contextFieldGroups}
        onEdit={onEdit}
        onDelete={onDelete}
        onAssignContextKey={onAssignContextKey}
        onUnmap={onUnmap}
      />
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Loading / Empty
// ---------------------------------------------------------------------------

const LoadingSkeleton = () => (
  <div className="space-y-2 p-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex items-center gap-2">
        <div className="h-5 w-10 animate-pulse rounded bg-zinc-200 dark:bg-zinc-700" />
        <div className="h-5 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-700" />
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TemplateFieldsPanel = ({
  templateId,
  templateFormat,
  readOnly,
  onReanalyze,
  isReanalyzing,
}: TemplateFieldsPanelProps) => {
  const { data, isLoading, error } = useTemplateFieldsQuery({ id: templateId });
  const { data: contextFieldsData } = useTemplateContextFieldsQuery();
  const [search, setSearch] = useState('');

  // CRUD state
  const [editingField, setEditingField] = useState<ReadTemplateField | null>(
    null,
  );
  const [deletingField, setDeletingField] = useState<ReadTemplateField | null>(
    null,
  );

  const [updateField] = useTemplateFieldUpdateMutation();

  // Generated type is singular ReadTemplateField but the list endpoint returns ReadTemplateField[].
  // Cast required until the OpenAPI spec is corrected (see v2-cutover-manifest.md).
  const fields = useMemo(
    () => (data as unknown as ReadTemplateField[] | undefined) ?? [],
    [data],
  );

  // ---- Pending mapping changes (local draft state) ----
  const [pendingChanges, setPendingChanges] = useState<Record<string, PendingFieldChange>>({});
  const [saving, setSaving] = useState(false);

  const pendingChangeCount = Object.keys(pendingChanges).length;
  const hasPendingChanges = pendingChangeCount > 0;
  const pendingFieldIds = useMemo(
    () => new Set(Object.keys(pendingChanges)),
    [pendingChanges],
  );

  // Merge server fields with pending changes for display
  const effectiveFields = useMemo(() => {
    if (!hasPendingChanges) return fields;
    return fields.map((f) => {
      const pending = pendingChanges[f.id as string];
      if (!pending) return f;
      return {
        ...f,
        source: pending.source,
        context_key: pending.context_key,
      } as ReadTemplateField;
    });
  }, [fields, pendingChanges, hasPendingChanges]);

  const contextFieldGroups: ContextFieldGroup[] = useMemo(() => {
    if (!contextFieldsData?.categories) return [];
    return contextFieldsData.categories.map((cat) => ({
      label: cat.label,
      options: cat.fields.map((f) => ({ id: f.key, name: f.label })),
    }));
  }, [contextFieldsData]);

  // Store mapping changes locally instead of firing API immediately
  const handleAssignContextKey = useCallback(
    (field: ReadTemplateField, contextKey: string) => {
      const serverField = fields.find((f) => f.id === field.id);
      // If mapping back to the server state, remove the pending change
      if (serverField?.source === 'CONTEXT' && serverField?.context_key === contextKey) {
        setPendingChanges((prev) => {
          const next = { ...prev };
          delete next[field.id as string];
          return next;
        });
      } else {
        setPendingChanges((prev) => ({
          ...prev,
          [field.id as string]: { source: 'CONTEXT', context_key: contextKey },
        }));
      }
    },
    [fields],
  );

  const handleUnmapField = useCallback(
    (field: ReadTemplateField) => {
      const serverField = fields.find((f) => f.id === field.id);
      // If unmapping back to server state, just remove the pending change
      if (serverField?.source === 'MANUAL') {
        setPendingChanges((prev) => {
          const next = { ...prev };
          delete next[field.id as string];
          return next;
        });
      } else {
        setPendingChanges((prev) => ({
          ...prev,
          [field.id as string]: { source: 'MANUAL', context_key: null },
        }));
      }
    },
    [fields],
  );

  // Save all pending mapping changes
  const handleSaveChanges = useCallback(async () => {
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(pendingChanges).map(([fieldId, change]) =>
          updateField({
            templateId,
            fieldId,
            body: { source: change.source, context_key: change.context_key },
          }).unwrap(),
        ),
      );
      const count = Object.keys(pendingChanges).length;
      setPendingChanges({});
      toast.success(`Saved ${count} field mapping${count > 1 ? 's' : ''}`);
    } catch {
      toast.error('Failed to save field mappings');
    } finally {
      setSaving(false);
    }
  }, [pendingChanges, templateId, updateField]);

  const handleDiscardChanges = useCallback(() => {
    setPendingChanges({});
  }, []);

  // Use effective fields for filtering and splitting
  const filteredFields = useMemo(() => {
    if (!search.trim()) return effectiveFields;
    const q = search.toLowerCase();
    return effectiveFields.filter(
      (f) =>
        f.reference.toLowerCase().includes(q) ||
        (f.context_key && f.context_key.toLowerCase().includes(q)),
    );
  }, [effectiveFields, search]);

  // Split into context (recognized), signing, and manual (unrecognized)
  const { contextFields, signingFields, manualFields } = useMemo(
    () =>
      filteredFields.reduce<{
        contextFields: ReadTemplateField[];
        signingFields: ReadTemplateField[];
        manualFields: ReadTemplateField[];
      }>(
        (acc, f) => {
          if (f.source === 'CONTEXT') {
            acc.contextFields.push(f);
          } else if (f.source === 'SIGNING') {
            acc.signingFields.push(f);
          } else {
            acc.manualFields.push(f);
          }
          return acc;
        },
        { contextFields: [], signingFields: [], manualFields: [] },
      ),
    [filteredFields],
  );

  const [deleteField, { isLoading: isDeleting }] =
    useTemplateFieldDeleteMutation();

  const handleDeleteConfirm = async () => {
    if (!deletingField) return;
    try {
      await deleteField({
        templateId,
        fieldId: deletingField.id as string,
      }).unwrap();
      // Clear any pending change for deleted field
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[deletingField.id as string];
        return next;
      });
      toast.success(`Field "${deletingField.reference}" deleted`);
    } catch {
      toast.error('Failed to delete field');
    }
    setDeletingField(null);
  };

  // Clear pending change when a field is saved through the editor dialog
  const handleEditSuccess = useCallback(() => {
    if (editingField) {
      setPendingChanges((prev) => {
        const next = { ...prev };
        delete next[editingField.id as string];
        return next;
      });
    }
    setEditingField(null);
  }, [editingField]);

  if (isLoading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700 dark:border-red-800/50 dark:bg-red-900/10 dark:text-red-400">
        Failed to load fields.
      </div>
    );
  }

  if (fields.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-zinc-300 py-10 text-center dark:border-zinc-700">
        <p className="text-sm font-medium text-zinc-500">
          No fields detected in this template
        </p>
        {onReanalyze && (
          <button
            type="button"
            onClick={onReanalyze}
            disabled={isReanalyzing}
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-zinc-900 disabled:opacity-50 dark:text-zinc-400 dark:hover:text-zinc-200"
          >
            <RefreshCw
              className={cn(
                'h-3.5 w-3.5',
                isReanalyzing && 'animate-spin',
              )}
            />
            {isReanalyzing ? 'Analyzing…' : 'Re-analyze'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Save/Discard bar for pending mapping changes */}
      {hasPendingChanges && (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-800/40 dark:bg-blue-900/10">
          <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">
            {pendingChangeCount} unsaved mapping change{pendingChangeCount > 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={saving}
              onClick={handleDiscardChanges}
              className="rounded px-2 py-0.5 text-[11px] font-medium text-zinc-600 hover:bg-blue-100 disabled:opacity-50 dark:text-zinc-400 dark:hover:bg-blue-800/30"
            >
              Discard
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={handleSaveChanges}
              className="inline-flex items-center gap-1 rounded bg-primary px-2 py-0.5 text-[11px] font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Save className="h-3 w-3" />
              )}
              Save
            </button>
          </div>
        </div>
      )}

      {/* Search + count */}
      <div className="flex items-center gap-2 px-1">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Filter variables..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-zinc-200 bg-white py-1.5 pl-8 pr-3 text-sm font-medium text-zinc-900 placeholder:text-zinc-500 transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
          />
        </div>
        <span className="inline-flex shrink-0 items-center rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium tabular-nums text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
          {filteredFields.length}
        </span>
        {onReanalyze && (
          <button
            type="button"
            onClick={onReanalyze}
            disabled={isReanalyzing}
            className="shrink-0 rounded-md p-1.5 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-50 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            title={isReanalyzing ? 'Analyzing…' : 'Re-analyze fields'}
          >
            <RefreshCw
              className={cn(
                'h-3.5 w-3.5',
                isReanalyzing && 'animate-spin',
              )}
            />
          </button>
        )}
      </div>

      {/* Context variables */}
      {contextFields.length > 0 && (
        <div>
          <FieldList
            fields={contextFields}
            readOnly={readOnly}
            pendingFieldIds={pendingFieldIds}
            contextFieldGroups={contextFieldGroups}
            onEdit={setEditingField}
            onDelete={setDeletingField}
            onAssignContextKey={handleAssignContextKey}
            onUnmap={handleUnmapField}
          />
        </div>
      )}

      {/* Signing fields */}
      {signingFields.length > 0 && (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 dark:border-zinc-700 dark:bg-zinc-800/30">
          <div className="flex items-center gap-1.5 px-3 py-2">
            <PenTool className="h-3.5 w-3.5 text-zinc-600 dark:text-zinc-400" />
            <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">
              Signing ({signingFields.length})
            </span>
          </div>
          <FieldList
            fields={signingFields}
            readOnly={readOnly}
            pendingFieldIds={pendingFieldIds}
            contextFieldGroups={contextFieldGroups}
            onEdit={setEditingField}
            onDelete={setDeletingField}
          />
        </div>
      )}

      {/* Manual / unrecognized fields */}
      {manualFields.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-800/40 dark:bg-amber-900/10">
          <div className="flex items-center gap-1.5 px-3 py-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-500" />
            <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">
              Unrecognized ({manualFields.length})
            </span>
          </div>
          <FieldList
            fields={manualFields}
            readOnly={readOnly}
            pendingFieldIds={pendingFieldIds}
            contextFieldGroups={contextFieldGroups}
            onEdit={setEditingField}
            onDelete={setDeletingField}
            onAssignContextKey={handleAssignContextKey}
          />
        </div>
      )}

      {filteredFields.length === 0 && search.trim() && (
        <p className="px-3 py-4 text-center text-sm font-medium text-zinc-500">
          No fields matching &ldquo;{search}&rdquo;
        </p>
      )}

      {/* Edit Field Editor */}
      {editingField && (
        <TemplateFieldEditor
          open={!!editingField}
          onClose={() => setEditingField(null)}
          templateId={templateId}
          templateFormat={templateFormat ?? ''}
          field={editingField}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deletingField}
        onOpenChange={(open) => !open && setDeletingField(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Field</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the field &quot;
              {deletingField?.reference}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
